import { Grid, makeStyles, MenuItem, TextField, Theme } from '@freighthub/silhouette-core'
import { containerTypeGroupTerms } from 'features/TransportUnits'
import { includes, max } from 'lodash-es'
import { FC } from 'react'
import { useController, useWatch } from 'react-hook-form'
import { useI18n } from 'utils/localisation'

import { FCLCargoContainerType } from '../../Cargo'
import { CARRIAGE_MODE_RAIL, CARRIAGE_MODE_SEA, carriageModeMap } from '../../Carriage'
import { QuoteRequestFormState } from '../quoteRequestUtils'
import { CommodityAutocomplete } from './CommodityAutocomplete'
import { DeleteRow } from './DeleteRow'
import { CargoType } from './domain'
import { useCargoTypeQuery } from './service'

const minimumCargoQuantity = 1
const minimumCargoWeightInKg = 0

type MaxWeightMap = Record<FCLCargoContainerType, Number>

const maxGrossWeightPerContainerMap: MaxWeightMap = {
  '22GP': 28000,
  '42GP': 26000,
  '45GP': 26000,
}

const maxGrossWeightPerContainerForUSMap: MaxWeightMap = {
  '22GP': 19960,
  '42GP': 19960,
  '45GP': 19820,
}

const useStyles = makeStyles((theme: Theme) => ({
  cargoDetailsRow: {
    padding: theme.spacing(1, 0),
    marginTop: theme.spacing(1),
  },
}))

type FCLCargoRowProps = {
  isDestinationUSA: boolean
  deleteCargo: null | (() => void)
  index: number
}

export const reduceCargoTypeListByAlias = (cargoTypeList: CargoType[]): string[] => {
  return cargoTypeList.reduce<string[]>((acc, { aliases }) => {
    // Get the tms alias group that contains the allowed container types
    const tmsAliasGroup = aliases.find(({ aliasType }) => aliasType === 'tms')
    if (!tmsAliasGroup) {
      return acc
    }
    const containerTypeGroup = tmsAliasGroup.values[0]

    if (!containerTypeGroup) {
      return acc
    }
    return [...acc, containerTypeGroup]
  }, [])
}

export type LeanFCLCargoRowProps = Omit<FCLCargoRowProps, 'index'> & {
  modes: string[]
  containerType: FCLCargoContainerType
  onContainerTypeChange: (containerType: FCLCargoContainerType) => void
  quantity: number
  onQuantityChange: (quantity: number) => void
  grossWeight: number
  onGrossWeightChange: (growthWeight: number) => void
  commodity: string
  onCommodityChange: (commodity: string) => void
  cargoTypeList: CargoType[]
}
export const LeanFCLCargoRow: FC<LeanFCLCargoRowProps> = ({
  cargoTypeList,
  deleteCargo,
  modes,
  isDestinationUSA,
  quantity,
  onQuantityChange,
  containerType,
  onContainerTypeChange,
  grossWeight,
  onGrossWeightChange,
  commodity,
  onCommodityChange,
}) => {
  const classes = useStyles()
  const { translate } = useI18n()
  const maxGrossWeightMap = isDestinationUSA ? maxGrossWeightPerContainerForUSMap : maxGrossWeightPerContainerMap

  const cargoTypesfilterdByMode = cargoTypeList.filter(({ transportModes }) =>
    includes(modes, 'sea')
      ? transportModes?.includes(carriageModeMap[CARRIAGE_MODE_SEA])
      : transportModes?.includes(carriageModeMap[CARRIAGE_MODE_RAIL])
  )

  const options = reduceCargoTypeListByAlias(cargoTypesfilterdByMode).map((containerTypeGroup) => {
    return {
      value: containerTypeGroup,
      text: translate(containerTypeGroupTerms[containerTypeGroup]),
    }
  })

  return (
    <Grid data-testid="fcl_cargo-row" container wrap="nowrap" spacing={2} className={classes.cargoDetailsRow}>
      <Grid xs={2}>
        <TextField
          fullWidth
          label={translate('quote_request-cargo-quantity')}
          type="number"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{ min: minimumCargoQuantity }}
          required
          value={quantity || ''}
          onChange={(e) => onQuantityChange(max([Number(e.target.value), minimumCargoQuantity]) as number)}
        />
      </Grid>
      <Grid xs>
        <TextField
          select
          fullWidth
          required
          label={translate('quote_request-cargo-select_container_type')}
          value={containerType}
          onChange={(e) => onContainerTypeChange(e.target.value as FCLCargoContainerType)}
          disabled={options.length <= 1}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.text}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid xs={3}>
        <TextField
          required
          fullWidth
          helperText={translate('quote_request-cargo-select_gross-weight-helper-text')}
          label={translate('quote_request-cargo-select_gross-weight')}
          type="number"
          inputProps={{
            min: minimumCargoWeightInKg,
            max: maxGrossWeightMap[containerType],
            'aria-label': translate('quote_request-cargo-select_gross-weight'),
            step: 0.001,
            'data-e2e-id': 'cargo-gross-weight-input',
          }}
          InputLabelProps={{
            shrink: true,
          }}
          value={grossWeight || ''}
          onChange={(e) => onGrossWeightChange(max([Number(e.target.value), minimumCargoWeightInKg]) as number)}
        />
      </Grid>
      <Grid xs={3}>
        <CommodityAutocomplete selectedValue={commodity || ''} onSelect={onCommodityChange} />
      </Grid>
      <Grid xs={1}>
        <DeleteRow deleteCargo={deleteCargo} />
      </Grid>
    </Grid>
  )
}

export const FCLCargoRow: FC<Omit<FCLCargoRowProps, 'cargoTypeList'>> = (props) => {
  const { data: cargoTypeList } = useCargoTypeQuery()
  const { index } = props
  const { field: quantityTypeField } = useController<QuoteRequestFormState, `cargoFcl.${number}.quantity`>({
    name: `cargoFcl.${index}.quantity`,
  })
  const { field: containerTypeField } = useController<QuoteRequestFormState, `cargoFcl.${number}.containerType`>({
    name: `cargoFcl.${index}.containerType`,
  })
  const { field: grossWeightField } = useController<QuoteRequestFormState, `cargoFcl.${number}.grossWeight`>({
    name: `cargoFcl.${index}.grossWeight`,
  })
  const { field: commodityTypeField } = useController<QuoteRequestFormState, `cargoFcl.${number}.commodityType`>({
    name: `cargoFcl.${index}.commodityType`,
  })

  const transportModes = useWatch<QuoteRequestFormState, 'modes'>({ name: 'modes' })

  return (
    <LeanFCLCargoRow
      cargoTypeList={cargoTypeList}
      {...props}
      quantity={quantityTypeField.value}
      onQuantityChange={quantityTypeField.onChange}
      containerType={containerTypeField.value}
      onContainerTypeChange={containerTypeField.onChange}
      grossWeight={grossWeightField.value ?? 0}
      onGrossWeightChange={grossWeightField.onChange}
      commodity={commodityTypeField.value ?? ''}
      onCommodityChange={commodityTypeField.onChange}
      modes={transportModes}
    />
  )
}
